// src/types/education-api.ts
// Patient-education article search — Flutter parity.
// Source: common_data_source_impl.dart:902 `fetchArticles` → GET {Master}/GetArticles?searchQuery=<q>
//         education_model.dart (ArticlesModel + Article).
// The response is a LIST of result-GROUPS, each carrying its own Articles[].
//
// WIRE CASING: the GetArticles response is PascalCase — Flutter's fromJson reads json["ResultType"] /
// json["Articles"] / json["Display"] / json["ArticleUrl"] (education_model.dart:39-43,80-84). We type
// the raw wire (…Wire) and map it to the lower-camel app shape in `useArticleSearch` so consumers use
// clean camelCase (matches the lab/referral search hooks).

/** Raw GetArticles hit (PascalCase wire). */
export interface ArticleWire {
  Id?: string;
  Display?: string;
  ArticleUrl?: string;
  Summary?: string;
}

/** Raw GetArticles result group (PascalCase wire). */
export interface ArticlesModelWire {
  ResultType?: string;
  Suggest?: string;
  ResultCount?: number;
  Articles?: ArticleWire[];
}

/** One education article (a search hit) — app shape. Flutter `Article` (education_model.dart). */
export interface Article {
  /** Backend `Id`. */
  id?: string;
  /** `Display` — the article title (shown in the row + persisted as AssessmentArticles.display). */
  display?: string;
  /** `ArticleUrl` — opened in the preview webview + persisted. */
  articleUrl?: string;
  /** `Summary` — short blurb (persisted; canvas card description falls back to 'Article'). */
  summary?: string;
}

/** One result GROUP from GetArticles. Flutter `ArticlesModel` (education_model.dart). */
export interface ArticlesModel {
  /** `ResultType` — the group label (e.g. content type), shown in the group header. */
  resultType?: string;
  /** `Suggest` — a suggested term; header falls back to the raw query when blank. */
  suggest?: string;
  /** `ResultCount` — count shown beside the group header. */
  resultCount?: number;
  /** `Articles` — the hits in this group. */
  articles?: Article[];
}

/** Map a raw PascalCase result group → the app shape. */
export function articlesModelFromWire(w: ArticlesModelWire): ArticlesModel {
  return {
    resultType: w.ResultType,
    suggest: w.Suggest,
    resultCount: w.ResultCount,
    articles: (w.Articles ?? []).map((a) => ({
      id: a.Id,
      display: a.Display,
      articleUrl: a.ArticleUrl,
      summary: a.Summary,
    })),
  };
}
